import React from 'react';
import { normalizeRotation, swapsAxes } from '../videoUpload/rotation';
import './RotatedMedia.css';

// Shows source footage the way it will render, by turning it in CSS.
//
// This is the whole reason footage rotation costs nothing: the file on disk
// keeps the camera's orientation, so every preview has to apply the correction
// itself. The alternative — baking rotation into the stored video — would mean
// a re-encode of the entire source every time the user nudged the control.
//
// ONLY wrap source footage. The rendered output (EditedResultPanel) already
// has the rotation baked in by FFmpeg; wrapping that too turns a 180° project
// upside down again, and the bug looks like the render ignoring the setting
// rather than the preview double-applying it.
//
// A half turn needs no layout change — the box is the same shape either way.
// A quarter turn does: a 16:9 source becomes 9:16, and a plain `rotate(90deg)`
// would spill out of a container still sized for landscape. So the frame takes
// the SWAPPED aspect ratio and the media inside it is sized in container-query
// units (`100cqh` wide by `100cqw` tall — the frame with its axes exchanged)
// before being turned about its centre. That keeps it pure CSS: no measuring,
// no resize observer, and nothing to re-run when the column width changes.
//
// The frame is rendered at EVERY angle, 0 included, and 0 is simply the angle
// with no transform rule behind it. This is about React identity, not styling:
// returning `children` bare at 0 and a wrapper otherwise puts a different
// element TYPE at this position, so turning a clip away from 0 (or back to it)
// unmounted the whole player subtree and mounted a fresh <video> — the clip
// jumped to 0:00, paused, and re-requested its media. With the rotate buttons
// now sitting on the picture itself that happened directly under the user's
// cursor, and only on the 0 boundary, so 90 -> 180 kept playing and 0 -> 90 did
// not. One stable wrapper makes every angle behave like every other.
const RotatedMedia = ({
  rotation,
  // Source pixel dimensions, used only to give the frame its swapped aspect
  // ratio on a quarter turn. Unknown until the server probe lands (the browser
  // cannot read MJPEG-in-MP4 metadata), so it falls back to 16:9 — the shape
  // of essentially every timelapse camera, and self-correcting the moment the
  // probe writes real numbers.
  width = null,
  height = null,
  className = '',
  // Burn-in previews drawn OVER the picture but NOT turned with it — today
  // MotionPace's speed dial (#533). It belongs in the frame rather than beside
  // it because the frame is the displayed picture box at every angle: on a
  // quarter turn it carries the swapped aspect ratio, so an overlay anchored to
  // it lands in the same corner of the same rectangle the viewer is looking at.
  // Keeping it OUT of `.tw-rot-inner` (the element that actually carries the
  // transform) is what keeps it upright, which is also what the render does —
  // the dial is composited after the rotation filter, not before it.
  overlay = null,
  children,
}) => {
  const angle = normalizeRotation(rotation);
  const quarter = swapsAxes(angle);
  const style = {};
  if (quarter) {
    const w = Number(width) > 0 ? Number(width) : 16;
    const h = Number(height) > 0 ? Number(height) : 9;
    style.aspectRatio = `${h} / ${w}`;
  }

  return (
    <div
      className={`tw-rot tw-rot--${angle}${quarter ? ' tw-rot--quarter' : ''}${className ? ` ${className}` : ''}`}
      style={style}
      data-testid="rotated-media"
      data-rotation={angle}
    >
      <div className="tw-rot-inner">{children}</div>
      {overlay}
    </div>
  );
};

export default RotatedMedia;
